import React, { useEffect, useMemo, useRef, useState } from 'react';
import Icon from '@/components/Icon';
import { ApiError, api, getCsrfToken } from '@/lib/api';

/**
 * Форма договора — создание и редактирование.
 *
 *   mode='create' student={...} defaultType='school'    → POST /api/agreements
 *   mode='edit'   student={...} agreement={...}         → PUT  /api/agreements/{id}
 *
 * Поток create:
 *   1. Если выбран новый заказчик — POST /api/signatories (все поля required для печати).
 *   2. POST /api/agreements (signatoryId обязателен).
 *   3. Backend сам заведёт onec_agreement и дёрнет push-one (если у ученика есть контрагент).
 */

const TYPES = [
  { id: 'school',       label: 'Школа',       icon: 'graduation', desc: 'Основное обучение, 0–4 класс' },
  { id: 'kindergarten', label: 'Детский сад', icon: 'users',      desc: 'Дошкольное отделение' },
  { id: 'paid_services', label: 'Доп. услуги', icon: 'sparkle',    desc: 'Кружки, продлёнка, питание' },
];

const todayIso = () => new Date().toISOString().slice(0, 10);
const fmtMoney = (n) => Number(n || 0).toLocaleString('ru-RU') + ' ₽';
const Agreement_displayNumber = (type, number) => {
  if (number === '' || number == null) return '';
  return type === 'paid_services' ? 'ДУ-' + number : String(number);
};

function pickDefaultYear(years) {
  const today = todayIso();
  const active = years.filter((y) => !y.archive);
  return active.find((y) => y.dateStart <= today && today <= y.dateEnd)
      || active[0]
      || years[0]
      || null;
}

function pickDates(year, type) {
  if (!year) return { dateStart: '', dateEnd: '' };
  const today = todayIso();
  const start = today > year.dateStart ? today : year.dateStart;
  const end = (type === 'kindergarten' && year.dateEndKindergarten)
    ? year.dateEndKindergarten
    : year.dateEnd;
  return { dateStart: start, dateEnd: end };
}

const EMPTY_NEW_SIGNATORY = {
  familyName: '',
  firstName: '',
  midName: '',
  sex: '',
  email: '',
  phone: '',
  passport: '',
  passportIssuedDate: '',
  passportIssuedBy: '',
  addressReg: '',
  addressFact: '',
};

export default function AgreementFormModal({ mode = 'create', student, agreement = {}, defaultType = 'school', onClose, onSaved, onDeleted, onLink }) {
  const isEdit = mode === 'edit';

  const [type, setType]           = useState(isEdit ? agreement.type : defaultType);
  const [years, setYears]         = useState([]);
  const [yearId, setYearId]       = useState(isEdit ? (agreement.yearStudyId || null) : null);
  const [number, setNumber]       = useState(isEdit ? (agreement.number || '') : '');
  const [date, setDate]           = useState(isEdit ? (agreement.date || todayIso()) : todayIso());
  const [dateStart, setDateStart] = useState(isEdit ? (agreement.dateStart || '') : '');
  const [dateEnd, setDateEnd]     = useState(isEdit ? (agreement.dateEnd || '') : '');

  // Заказчик: выбираем из контактов ученика. Если новый — собираем форму на лету.
  // Если существующий, у которого не хватает полей для договора — также показываем форму
  // (на dozaполнение), затем PUT /api/contacts/{id}.
  const [contacts, setContacts]           = useState([]);
  const [signatoryMode, setSignatoryMode] = useState('existing'); // existing | new
  const [signatoryId, setSignatoryId]     = useState(isEdit && agreement.signatoryId ? String(agreement.signatoryId) : '');
  const [newSig, setNewSig]               = useState(EMPTY_NEW_SIGNATORY);

  // Школа/Сад
  const [tariffs, setTariffs]           = useState([]);
  const [tariffId, setTariffId]         = useState(isEdit && agreement.tariffId ? String(agreement.tariffId) : '');
  const [admissionFee, setAdmissionFee] = useState(isEdit && agreement.admissionFee != null ? String(agreement.admissionFee) : '');
  const [admissionDue, setAdmissionDue] = useState(isEdit ? (agreement.admissionFeeDueDate || '') : '');

  // Школа
  const [klass, setKlass] = useState(isEdit && agreement.class != null ? String(agreement.class) : '');

  // Подписан / расторгнут
  const [signed, setSigned] = useState(isEdit ? !!agreement.signed : false);
  const [terminated, setTerminated] = useState(isEdit ? !!agreement.dateTermination : false);
  const [terminationDate, setTerminationDate] = useState(isEdit ? (agreement.dateTermination || todayIso()) : todayIso());
  const [terminationReason, setTerminationReason] = useState(isEdit ? (agreement.terminationReason || '') : '');
  const [terminationReasonDetails, setTerminationReasonDetails] = useState(
    isEdit ? (agreement.terminationReasonDetails || '') : ''
  );

  // Скан подписанного — присутствует только в edit-режиме (нужен id договора)
  const [scan, setScan]           = useState(isEdit ? (agreement.scan || null) : null);
  const [uploading, setUploading] = useState(false);

  const [loadingMeta, setLoadingMeta] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  // Справочники
  useEffect(() => {
    let aborted = false;
    setLoadingMeta(true);
    Promise.all([
      api.get('/api/years_study'),
      api.get(`/api/contacts?student_id=${student.id}`),
    ]).then(([yres, sres]) => {
      if (aborted) return;
      const ys = (yres.rows || []).filter((y) => !y.archive);
      setYears(ys);
      if (!isEdit) {
        const def = pickDefaultYear(ys);
        if (def) {
          setYearId(def.id);
          const d = pickDates(def, type);
          setDateStart(d.dateStart);
          setDateEnd(d.dateEnd);
        }
      }
      const list = sres.rows || [];
      setContacts(list);
      if (!isEdit && list.length) {
        setSignatoryMode('existing');
        setSignatoryId(String(list[0].id));
      } else if (!isEdit) {
        setSignatoryMode('new');
      }
    }).catch((e) => {
      if (!aborted) setError(e instanceof ApiError ? e.message : 'Ошибка загрузки');
    }).finally(() => {
      if (!aborted) setLoadingMeta(false);
    });
    return () => { aborted = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [student.id]);

  // Тарифы при смене типа
  useEffect(() => {
    if (type === 'paid_services') { setTariffs([]); return; }
    let aborted = false;
    api.get(`/api/tariffs?type=${type}`).then((res) => {
      if (aborted) return;
      const ts = res.rows || [];
      setTariffs(ts);
      if (ts.length && !ts.find((t) => String(t.id) === String(tariffId))) {
        if (!isEdit) setTariffId('');
      }
    }).catch(() => { if (!aborted) setTariffs([]); });
    return () => { aborted = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  // Подсказка номера для create. Логируем ошибки в консоль — иначе тихо «теряются»
  // и поле остаётся пустым, что выглядит как «автонумератор не работает».
  useEffect(() => {
    if (isEdit) return;
    let aborted = false;
    api.get(`/api/agreements/next-number?type=${type}`)
      .then((r) => {
        if (aborted) return;
        if (r && r.number != null) setNumber(String(r.number));
        else console.warn('next-number: пустой ответ', r);
      })
      .catch((e) => console.error('next-number failed:', e));
    return () => { aborted = true; };
  }, [type, isEdit]);

  // Авто-даты при смене года/типа (только create — на edit не перезатираем пользовательские значения)
  useEffect(() => {
    if (isEdit || !yearId) return;
    const y = years.find((yy) => yy.id === yearId);
    if (!y) return;
    const d = pickDates(y, type);
    setDateStart(d.dateStart);
    setDateEnd(d.dateEnd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearId, type]);

  // Авто-взнос при выборе тарифа
  useEffect(() => {
    if (!tariffId || isEdit) return;
    const t = tariffs.find((tt) => String(tt.id) === String(tariffId));
    if (t && (admissionFee === '' || admissionFee === null)) {
      setAdmissionFee(String(t.price));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tariffId]);

  const showTariff = type !== 'paid_services';
  const showClass  = type === 'school';

  // Подбор информации о выбранном контакте.
  const pickedContact = useMemo(() => {
    if (signatoryMode !== 'existing' || !signatoryId) return null;
    return contacts.find((c) => String(c.id) === String(signatoryId)) || null;
  }, [signatoryMode, signatoryId, contacts]);

  // Если у выбранного контакта не хватает полей для печатной формы — поднимем форму
  // дозаполнения (с пред-заполнением текущими значениями).
  const missingForSig = pickedContact?.missingForSignatory || [];
  const needsFill     = signatoryMode === 'existing' && pickedContact && missingForSig.length > 0;
  const showInlineForm = signatoryMode === 'new' || needsFill;

  // При смене выбранного контакта на «требует дозаполнения» — переливаем его данные в форму.
  useEffect(() => {
    if (!needsFill || !pickedContact) return;
    setNewSig({
      familyName:         pickedContact.familyName || '',
      firstName:          pickedContact.firstName || '',
      midName:            pickedContact.midName || '',
      sex:                pickedContact.sex || '',
      email:              pickedContact.email || '',
      phone:              pickedContact.phone || '',
      passport:           pickedContact.passport || '',
      passportIssuedDate: pickedContact.passportIssuedDate || '',
      passportIssuedBy:   pickedContact.passportIssuedBy || '',
      addressReg:         pickedContact.addressReg || '',
      addressFact:        pickedContact.addressFact || '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedContact?.id, needsFill]);

  const setNewSigField = (k, v) => setNewSig((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError(null);

    try {
      // Валидация заказчика на стороне фронта (бэк всё равно проверит)
      if (signatoryMode === 'existing' && !signatoryId) {
        throw new Error('Выберите заказчика или создайте нового');
      }

      const sigPayload = {
        studentId:          student.id,
        familyName:         newSig.familyName.trim(),
        firstName:          newSig.firstName.trim(),
        midName:            newSig.midName.trim(),
        sex:                newSig.sex,
        email:              newSig.email.trim(),
        phone:              newSig.phone.trim(),
        passport:           newSig.passport.trim(),
        passportIssuedDate: newSig.passportIssuedDate,
        passportIssuedBy:   newSig.passportIssuedBy.trim(),
        addressReg:         newSig.addressReg.trim(),
        addressFact:        newSig.addressFact.trim(),
      };

      let finalSignatoryId = null;
      if (signatoryMode === 'new') {
        const res = await api.post('/api/contacts', sigPayload);
        finalSignatoryId = res.contact.id;
      } else if (needsFill) {
        // Дозаполняем существующий контакт — у него нехватает полей для печати договора.
        const res = await api.put(`/api/contacts/${signatoryId}`, sigPayload);
        finalSignatoryId = res.contact.id;
      } else {
        finalSignatoryId = Number(signatoryId);
      }

      const agrPayload = {
        date,
        dateStart:   dateStart || null,
        dateEnd:     dateEnd || null,
        signatoryId: finalSignatoryId,
        yearStudyId: yearId || null,
        number:      number.trim() || undefined,
        signed,
        dateTermination:   terminated ? (terminationDate || null) : null,
        terminationReason: terminated ? (terminationReason || null) : null,
        // Подробности отправляем только если выбрана конкретная причина,
        // к которой они относятся. Иначе бэк всё равно их обнулит, но
        // лучше не таскать «мёртвый» payload.
        terminationReasonDetails: terminated && terminationReason === 'Перевод в другую школу'
          ? (terminationReasonDetails.trim() || null)
          : null,
      };
      if (!isEdit) {
        agrPayload.studentId = student.id;
        agrPayload.type = type;
      }
      if (showTariff) {
        agrPayload.tariffId            = tariffId ? Number(tariffId) : null;
        agrPayload.admissionFee        = admissionFee !== '' ? Number(admissionFee) : null;
        agrPayload.admissionFeeDueDate = admissionDue || null;
      }
      if (showClass) {
        agrPayload.class = klass !== '' ? Number(klass) : null;
      }

      const res = isEdit
        ? await api.put(`/api/agreements/${agreement.id}`, agrPayload)
        : await api.post('/api/agreements', agrPayload);
      onSaved?.(res.agreement);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err.message || 'Не удалось сохранить'));
    } finally {
      setSaving(false);
    }
  };

  const fio = `${student.familyName} ${student.firstName}${student.midName ? ' ' + student.midName : ''}`.trim();

  // Если редактируем старый договор с архивным тарифом — он отсутствует
  // в загруженном списке активных. Подкладываем «синтетический» объект
  // тарифа из данных самого договора, чтобы breakdown по месяцам считался
  // и сумма к оплате не была пустой.
  const tariff = useMemo(() => {
    const fromList = tariffs.find((t) => String(t.id) === String(tariffId));
    if (fromList) return fromList;
    if (isEdit && tariffId && agreement.tariffPrice != null) {
      return {
        id:       Number(tariffId),
        name:     agreement.tariffName || `Тариф #${tariffId}`,
        price:    Number(agreement.tariffPrice),
        archive:  true, // флажок для UI, если когда-нибудь захочется отметить
      };
    }
    return undefined;
  }, [tariffs, tariffId, isEdit, agreement.tariffName, agreement.tariffPrice]);
  const typeMeta = TYPES.find((t) => t.id === type) || TYPES[0];

  // Эффективная дата окончания: если договор расторгнут — до terminationDate,
  // но не позднее dateEnd. Иначе — dateEnd.
  const effectiveEnd = (() => {
    if (terminated && terminationDate) {
      if (!dateEnd) return terminationDate;
      return terminationDate < dateEnd ? terminationDate : dateEnd;
    }
    return dateEnd;
  })();

  // Загружаем производственный календарь для лет, задеваемых периодом
  // действия договора. Без этого нельзя посчитать пропорциональную оплату
  // неполных месяцев (см. costBreakdown).
  const [holidaysByDate, setHolidaysByDate] = useState(() => new Map());
  useEffect(() => {
    if (!dateStart || !effectiveEnd) return;
    const yStart = parseInt(dateStart.slice(0, 4), 10);
    const yEnd   = parseInt(effectiveEnd.slice(0, 4), 10);
    if (!yStart || !yEnd) return;
    const years = [];
    for (let y = yStart; y <= yEnd; y++) years.push(y);
    let aborted = false;
    Promise.all(years.map((y) =>
      api.get(`/api/holidays?year=${y}`).then((r) => r.rows || []).catch(() => [])
    )).then((all) => {
      if (aborted) return;
      const m = new Map();
      for (const rows of all) for (const d of rows) m.set(d.date, d);
      setHolidaysByDate(m);
    });
    return () => { aborted = true; };
  }, [dateStart, effectiveEnd]);

  // Помесячная разбивка стоимости договора:
  //   • полный месяц = полная плата по тарифу
  //   • неполный месяц = price × workingInRange / workingTotal,
  //     где workingTotal — рабочие дни во всём месяце по производственному календарю
  //     (каникулы и предпраздничные считаем рабочими; subcategory: holiday → не рабочий,
  //      workday — переопределяет выходной).
  // Округление каждой месячной суммы до рубля: копейки ≤ 50 → вниз, ≥ 51 → вверх.
  const costBreakdown = useMemo(() => {
    if (!showTariff || !tariff || !dateStart || !effectiveEnd) {
      return { months: [], monthsCount: 0, tariffTotal: 0, total: 0 };
    }
    const start = new Date(dateStart + 'T00:00:00');
    const end   = new Date(effectiveEnd + 'T00:00:00');
    if (isNaN(+start) || isNaN(+end) || end < start) {
      return { months: [], monthsCount: 0, tariffTotal: 0, total: 0 };
    }
    // ISO-строка по локальной дате (избегаем сюрпризов часовых поясов).
    const isoLocal = (y, m, d) =>
      `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const isWorkingDay = (y, m, d) => {
      const meta = holidaysByDate.get(isoLocal(y, m, d));
      if (meta) {
        if (meta.kind === 'holiday') return false;
        if (meta.kind === 'workday') return true;
      }
      const dow = new Date(y, m, d).getDay();
      return dow !== 0 && dow !== 6;
    };
    const roundRub = (raw) => {
      const cents = Math.round(raw * 100);
      const rub = Math.floor(cents / 100);
      const kop = cents - rub * 100;
      return rub + (kop >= 51 ? 1 : 0);
    };
    const months = [];
    let cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    const lastMonth = new Date(end.getFullYear(), end.getMonth(), 1);
    while (cursor <= lastMonth) {
      const y = cursor.getFullYear();
      const m = cursor.getMonth();
      const daysInMonth = new Date(y, m + 1, 0).getDate();
      let workingTotal = 0;
      for (let d = 1; d <= daysInMonth; d++) {
        if (isWorkingDay(y, m, d)) workingTotal++;
      }
      const rangeStartDay = (y === start.getFullYear() && m === start.getMonth()) ? start.getDate() : 1;
      const rangeEndDay   = (y === end.getFullYear()   && m === end.getMonth())   ? end.getDate()   : daysInMonth;
      let workingInRange = 0;
      for (let d = rangeStartDay; d <= rangeEndDay; d++) {
        if (isWorkingDay(y, m, d)) workingInRange++;
      }
      const isFullMonth = rangeStartDay === 1 && rangeEndDay === daysInMonth;
      let amount;
      if (isFullMonth) {
        amount = roundRub(Number(tariff.price));
      } else if (workingTotal === 0) {
        amount = 0;
      } else {
        amount = roundRub(Number(tariff.price) * workingInRange / workingTotal);
      }
      months.push({ year: y, month: m, isFullMonth, workingInRange, workingTotal, amount });
      cursor = new Date(y, m + 1, 1);
    }
    const tariffTotal = months.reduce((s, x) => s + x.amount, 0);
    const total = tariffTotal + (Number(admissionFee) || 0);
    return { months, monthsCount: months.length, tariffTotal, total };
  }, [showTariff, tariff, dateStart, effectiveEnd, admissionFee, holidaysByDate]);

  // Загрузка скана. Поддерживается и клик, и drag-and-drop.
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const onPickFile = () => fileInputRef.current?.click();

  const ALLOWED_MIME = ['application/pdf', 'image/jpeg', 'image/png'];
  const MAX_BYTES = 50 * 1024 * 1024;

  const uploadScanFile = async (f) => {
    if (!f || !isEdit) return;
    if (!ALLOWED_MIME.includes(f.type)) {
      setError('Допустимы только PDF, JPEG и PNG');
      return;
    }
    if (f.size > MAX_BYTES) {
      setError('Файл больше 50 МБ');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('scan', f);
      const res = await fetch(`/api/agreements/${agreement.id}/scan`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-CSRF-Token': getCsrfToken() || '' },
        body: fd,
      });
      const text = await res.text();
      const data = text ? JSON.parse(text) : null;
      if (!res.ok) throw new Error(data?.error?.message || 'Не удалось загрузить файл');
      setScan(data?.agreement?.scan || null);
    } catch (err) {
      setError(err.message || 'Ошибка загрузки');
    } finally { setUploading(false); }
  };

  const onFileChosen = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    await uploadScanFile(f);
  };

  // Drag-and-drop handlers — навешиваем на drop-зону.
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); if (!dragOver) setDragOver(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };
  const onDrop = async (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) await uploadScanFile(f);
  };
  const onDeleteAgreement = async () => {
    if (!isEdit) return;
    const num = Agreement_displayNumber(agreement.type, agreement.number);
    if (!confirm(`Удалить договор ${num}? Действие необратимо.`)) return;
    setSaving(true); setError(null);
    try {
      await api.delete(`/api/agreements/${agreement.id}`);
      onDeleted?.(agreement.id);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err.message || 'Не удалось удалить'));
      setSaving(false);
    }
  };

  const onRemoveScan = async () => {
    if (!isEdit || !scan) return;
    if (!confirm('Удалить загруженный скан?')) return;
    setUploading(true); setError(null);
    try {
      await api.delete(`/api/agreements/${agreement.id}/scan`);
      setScan(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось удалить');
    } finally { setUploading(false); }
  };
  const fmtFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' КБ';
    return (bytes / 1024 / 1024).toFixed(1) + ' МБ';
  };
  const fmtDateRu = (iso) => iso ? iso.split('-').reverse().join('.') : '—';

  // «Иванова Елена Михайловна» → «Иванова Е. М.»
  const shortenName = (full) => {
    if (!full) return '';
    const parts = String(full).trim().split(/\s+/);
    if (parts.length <= 1) return parts[0] || '';
    const initials = parts.slice(1).map((p) => p[0] ? p[0].toUpperCase() + '.' : '').filter(Boolean).join(' ');
    return parts[0] + (initials ? ' ' + initials : '');
  };

  return (
    <div className="sd-overlay" onClick={(e) => { if (e.target.classList.contains('sd-overlay')) onClose(); }}>
      <div className="sd-modal sd-modal--wide" role="dialog" aria-modal="true">
        <div className="sd-edit__head">
          <div>
            <h2 style={{ margin: 0 }}>
              {isEdit ? 'Редактирование договора' : 'Новый договор'}
              {isEdit && agreement.number && (
                <span style={{ color: 'var(--fg-muted)', fontWeight: 400, marginLeft: 8 }}>
                  № {agreement.displayNumber || agreement.number}
                </span>
              )}
            </h2>
            <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 2 }}>
              <Icon name="users" size={11} /> {fio}
              {student.class?.label && <> · {student.class.label}</>}
            </div>
          </div>
          <button className="sd-close" onClick={onClose}><Icon name="x" size={14} /></button>
        </div>

        {loadingMeta ? (
          <div style={{ padding: 32, color: 'var(--fg-muted)' }}>Загрузка…</div>
        ) : (
          <form onSubmit={submit} className="sd-edit__form" noValidate>
            <div className="agr-modal__body">
              <div className="agr-modal__main">
              <div className="sd-edit__field sd-edit__field--full">
                <label className="sd-edit__label">Тип договора</label>
                <div className="agr-types">
                  {TYPES.map((t) => (
                    <button
                      type="button"
                      key={t.id}
                      className={'agr-type' + (type === t.id ? ' is-active' : '')}
                      onClick={() => !isEdit && setType(t.id)}
                      disabled={isEdit}
                      title={isEdit ? 'Тип нельзя изменить после создания' : undefined}
                    >
                      <span className={'agr-type__icon agr-type__icon--' + t.id}>
                        <Icon name={t.icon} size={16} />
                      </span>
                      <span className="agr-type__main">
                        <span className="agr-type__label">{t.label}</span>
                        <span className="agr-type__desc">{t.desc}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="sd-edit__field">
                <label className="sd-edit__label">Учебный год *</label>
                <select
                  className="sd-edit__select"
                  value={yearId || ''}
                  onChange={(e) => setYearId(Number(e.target.value) || null)}
                  required
                >
                  <option value="">— выберите —</option>
                  {years.map((y) => (
                    <option key={y.id} value={y.id}>{y.name}</option>
                  ))}
                </select>
              </div>
              <div className="sd-edit__field">
                <label className="sd-edit__label">
                  Номер договора
                  {type === 'paid_services' && number && (
                    <span style={{ color: 'var(--fg-muted)', fontWeight: 400 }}>
                      {' · отобразится как '}<b style={{ color: 'var(--fg)' }}>ДУ-{number}</b>
                    </span>
                  )}
                </label>
                <input
                  className="sd-edit__input"
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                  placeholder="автонумератор"
                />
              </div>

              <div className="sd-edit__field">
                <label className="sd-edit__label">Дата договора *</label>
                <input
                  className="sd-edit__input"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                />
              </div>
              <div className="sd-edit__field"></div>
              <div className="sd-edit__field">
                <label className="sd-edit__label">Срок действия с</label>
                <input
                  className="sd-edit__input"
                  type="date"
                  value={dateStart}
                  onChange={(e) => setDateStart(e.target.value)}
                />
              </div>
              <div className="sd-edit__field">
                <label className="sd-edit__label">Срок действия до</label>
                <input
                  className="sd-edit__input"
                  type="date"
                  value={dateEnd}
                  onChange={(e) => setDateEnd(e.target.value)}
                />
              </div>

              {showTariff && (
                <>
                  <div className="sd-edit__field sd-edit__field--full">
                    <label className="sd-edit__label">Ставка месячная *</label>
                    <select
                      className="sd-edit__select"
                      value={tariffId || ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        setTariffId(v);
                        const t = tariffs.find((tt) => String(tt.id) === String(v));
                        if (t) setAdmissionFee(String(t.price));
                      }}
                      required
                    >
                      <option value="">— выберите тариф —</option>
                      {/* На редактировании старых договоров тариф мог уйти
                          в архив — в `tariffs` (активные) его уже нет.
                          Добавляем дополнительную опцию по данным самого
                          договора (`agreement.tariffName/tariffPrice`),
                          чтобы селект не выглядел «не выбрано». */}
                      {isEdit && tariffId && !tariffs.find((t) => String(t.id) === String(tariffId)) && (
                        <option value={tariffId}>
                          {agreement.tariffName || `Тариф #${tariffId}`}
                          {agreement.tariffPrice != null ? ` — ${fmtMoney(agreement.tariffPrice)} / мес` : ''}
                          {' (архив)'}
                        </option>
                      )}
                      {tariffs.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name} — {fmtMoney(t.price)} / мес
                        </option>
                      ))}
                    </select>
                    {isEdit && tariffId && !tariffs.find((t) => String(t.id) === String(tariffId)) && (
                      <div className="sd-edit__hint" style={{ color: 'var(--orange-700, #b45309)' }}>
                        Текущий тариф в архиве. Если не меняете — оставьте как есть. Чтобы выбрать
                        другой, разверните список — там только активные тарифы.
                      </div>
                    )}
                    {tariffs.length === 0 && !tariffId && (
                      <div className="sd-edit__hint">
                        Нет активных тарифов для типа «{TYPES.find((t) => t.id === type)?.label}». Добавьте в разделе «Финансы → Тарифы».
                      </div>
                    )}
                  </div>

                  <div className="sd-edit__field">
                    <label className="sd-edit__label">Вступительный взнос, ₽</label>
                    <input
                      className="sd-edit__input"
                      type="number"
                      min="0"
                      step="0.01"
                      value={admissionFee}
                      onChange={(e) => setAdmissionFee(e.target.value)}
                    />
                  </div>
                  <div className="sd-edit__field">
                    <label className="sd-edit__label">Оплатить взнос до</label>
                    <input
                      className="sd-edit__input"
                      type="date"
                      value={admissionDue}
                      onChange={(e) => setAdmissionDue(e.target.value)}
                    />
                  </div>
                </>
              )}

              {showClass && (
                <div className="sd-edit__field">
                  <label className="sd-edit__label">Класс *</label>
                  <input
                    className="sd-edit__input"
                    type="number"
                    min="0"
                    max="11"
                    value={klass}
                    onChange={(e) => setKlass(e.target.value)}
                    required
                    placeholder="0..4"
                  />
                </div>
              )}

              <div className="sd-edit__field sd-edit__field--full">
                <label className="sd-edit__label">Заказчик *</label>
                <select
                  className="sd-edit__select"
                  value={signatoryMode === 'new' ? '__new__' : (signatoryId || '')}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '__new__') { setSignatoryMode('new'); setSignatoryId(''); setNewSig(EMPTY_NEW_SIGNATORY); }
                    else { setSignatoryMode('existing'); setSignatoryId(v); }
                  }}
                  required={signatoryMode !== 'new'}
                >
                  <option value="">— выберите —</option>
                  {contacts.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.fullName}
                      {c.role ? ` · ${c.role}` : ''}
                      {c.missingForSignatory && c.missingForSignatory.length > 0 ? ' · нужно дозаполнить' : ''}
                    </option>
                  ))}
                  <option value="__new__">+ Создать нового…</option>
                </select>
              </div>

              {showInlineForm && (
                <div className="sd-edit__inline-block">
                  <div className="sd-edit__inline-title">
                    {signatoryMode === 'new'
                      ? 'Новый контакт · все поля обязательны для договора'
                      : `Дозаполните контакт «${pickedContact?.fullName}» · нужно для печати договора`}
                  </div>
                  {signatoryMode === 'existing' && missingForSig.length > 0 && (
                    <div style={{ gridColumn: '1 / -1', fontSize: 11.5, color: 'var(--amber-fg)' }}>
                      Не хватает: {missingForSig.join(', ')}.
                    </div>
                  )}
                  <div className="sd-edit__field">
                    <label className="sd-edit__label">Фамилия *</label>
                    <input className="sd-edit__input" value={newSig.familyName}
                      onChange={(e) => setNewSigField('familyName', e.target.value)} required />
                  </div>
                  <div className="sd-edit__field">
                    <label className="sd-edit__label">Имя *</label>
                    <input className="sd-edit__input" value={newSig.firstName}
                      onChange={(e) => setNewSigField('firstName', e.target.value)} required />
                  </div>
                  <div className="sd-edit__field sd-edit__field--full">
                    <label className="sd-edit__label">Отчество *</label>
                    <input className="sd-edit__input" value={newSig.midName}
                      onChange={(e) => setNewSigField('midName', e.target.value)} required />
                  </div>
                  <div className="sd-edit__field">
                    <label className="sd-edit__label">Пол *</label>
                    <select className="sd-edit__select" value={newSig.sex}
                      onChange={(e) => setNewSigField('sex', e.target.value)} required>
                      <option value="">— выберите —</option>
                      <option value="male">мужской</option>
                      <option value="female">женский</option>
                    </select>
                  </div>
                  <div className="sd-edit__field"></div>

                  <div className="sd-edit__field">
                    <label className="sd-edit__label">Телефон *</label>
                    <input className="sd-edit__input" type="tel" value={newSig.phone}
                      onChange={(e) => setNewSigField('phone', e.target.value)} required />
                  </div>
                  <div className="sd-edit__field">
                    <label className="sd-edit__label">Email</label>
                    <input className="sd-edit__input" type="email" value={newSig.email}
                      onChange={(e) => setNewSigField('email', e.target.value)} />
                  </div>

                  <div className="sd-edit__field">
                    <label className="sd-edit__label">Паспорт (серия и номер) *</label>
                    <input className="sd-edit__input" value={newSig.passport}
                      onChange={(e) => setNewSigField('passport', e.target.value)}
                      placeholder="1234 567890" required />
                  </div>
                  <div className="sd-edit__field">
                    <label className="sd-edit__label">Дата выдачи *</label>
                    <input className="sd-edit__input" type="date" value={newSig.passportIssuedDate}
                      onChange={(e) => setNewSigField('passportIssuedDate', e.target.value)} required />
                  </div>
                  <div className="sd-edit__field sd-edit__field--full">
                    <label className="sd-edit__label">Кем выдан *</label>
                    <input className="sd-edit__input" value={newSig.passportIssuedBy}
                      onChange={(e) => setNewSigField('passportIssuedBy', e.target.value)} required />
                  </div>

                  <div className="sd-edit__field sd-edit__field--full">
                    <label className="sd-edit__label">Адрес регистрации *</label>
                    <input className="sd-edit__input" value={newSig.addressReg}
                      onChange={(e) => setNewSigField('addressReg', e.target.value)} required />
                  </div>
                  <div className="sd-edit__field sd-edit__field--full">
                    <label className="sd-edit__label">Адрес фактический *</label>
                    <input className="sd-edit__input" value={newSig.addressFact}
                      onChange={(e) => setNewSigField('addressFact', e.target.value)}
                      placeholder="можно тот же, что регистрация" required />
                  </div>
                </div>
              )}

              {/* === Подписан === */}
              <div className={'agr-term-block' + (signed ? ' is-on agr-term-block--signed' : '')}>
                <input
                  type="checkbox"
                  className="agr-term-block__check"
                  id={`agr-signed-${agreement?.id || 'new'}`}
                  checked={signed}
                  onChange={(e) => setSigned(e.target.checked)}
                />
                <label className="agr-term-block__main" htmlFor={`agr-signed-${agreement?.id || 'new'}`}>
                  <span className="agr-term-block__title">Договор подписан</span>
                  <span className="agr-term-block__sub">Отметьте, когда договор подписан обеими сторонами. Только подписанные договоры пускают в зачисление.</span>
                </label>
                {signed && <span className="agr-term-block__pill agr-term-block__pill--ok">подписан</span>}
              </div>

              {/* === Досрочное расторжение === */}
              <div className={'agr-term-block' + (terminated ? ' is-on' : '')}>
                <input
                  type="checkbox"
                  className="agr-term-block__check"
                  id={`agr-term-${agreement?.id || 'new'}`}
                  checked={terminated}
                  onChange={(e) => setTerminated(e.target.checked)}
                />
                <label className="agr-term-block__main" htmlFor={`agr-term-${agreement?.id || 'new'}`}>
                  <span className="agr-term-block__title">Договор расторгнут досрочно</span>
                  <span className="agr-term-block__sub">Включите, если договор прекращён до планового окончания</span>
                </label>
                {terminated && <span className="agr-term-block__pill">расторгнут</span>}
                {terminated && (
                  <div className="agr-term-block__fields">
                    <div className="sd-edit__field">
                      <label className="sd-edit__label">Дата расторжения *</label>
                      <input
                        className="sd-edit__input"
                        type="date"
                        value={terminationDate}
                        max={dateEnd || undefined}
                        onChange={(e) => setTerminationDate(e.target.value)}
                        required
                      />
                      {dateEnd && (
                        <div className="agr-term-block__hint">Должна быть не позже {fmtDateRu(dateEnd)}</div>
                      )}
                    </div>
                    <div className="sd-edit__field">
                      <label className="sd-edit__label">Причина</label>
                      <select
                        className="sd-edit__select"
                        value={terminationReason}
                        onChange={(e) => {
                          const v = e.target.value;
                          setTerminationReason(v);
                          // Сбрасываем подробности, если причина больше не «перевод».
                          if (v !== 'Перевод в другую школу') setTerminationReasonDetails('');
                        }}
                      >
                        <option value="">— не указана —</option>
                        <option value="Перевод в другую школу">Перевод в другую школу</option>
                        <option value="Семейные обстоятельства">Семейные обстоятельства</option>
                        <option value="Финансовые обстоятельства">Финансовые обстоятельства</option>
                        <option value="Окончание обучения">Окончание обучения</option>
                        <option value="Иное">Иное</option>
                      </select>
                    </div>
                    {/* Подробности — только для «Перевод в другую школу».
                        Необязательное, до 500 символов. */}
                    {terminationReason === 'Перевод в другую школу' && (
                      <div className="sd-edit__field sd-edit__field--full">
                        <label className="sd-edit__label">Подробности: в какую школу</label>
                        <input
                          type="text"
                          className="sd-edit__input"
                          maxLength={500}
                          value={terminationReasonDetails}
                          onChange={(e) => setTerminationReasonDetails(e.target.value)}
                          placeholder="Например: ГБОУ № 1234, Москва"
                        />
                        <div className="sd-edit__hint">
                          Необязательно. Сохраним для статистики «куда уходят ученики».
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* === Скан подписанного договора (только в edit-режиме — нужен id) === */}
              {isEdit && (
                <div className="agr-scan">
                  <div className="agr-scan__title">Скан подписанного договора</div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="application/pdf,image/jpeg,image/png"
                    style={{ display: 'none' }}
                    onChange={onFileChosen}
                  />
                  {scan ? (
                    <div
                      className={'agr-scan__card' + (dragOver ? ' is-dragover' : '')}
                      onDragOver={onDragOver}
                      onDragLeave={onDragLeave}
                      onDrop={onDrop}
                    >
                      <ScanFileIcon mime={scan.mime} />
                      <div className="agr-scan__main">
                        <div className="agr-scan__name">{scan.originalName}</div>
                        <div className="agr-scan__meta">
                          {fmtFileSize(scan.size)}
                          {scan.uploadedAt && <> · загружен {scan.uploadedAt.slice(0, 10).split('-').reverse().join('.')}</>}
                          {scan.uploadedByName && <> · {shortenName(scan.uploadedByName)}</>}
                        </div>
                        <div className="agr-scan__status">
                          <Icon name="check" size={11} />
                          <span>Подписанный экземпляр в архиве</span>
                        </div>
                      </div>
                      <div className="agr-scan__actions">
                        <a
                          className="agr-scan__iconbtn"
                          href={scan.downloadUrl}
                          target="_blank"
                          rel="noreferrer"
                          title="Открыть"
                        >
                          <Icon name="eye" size={14} />
                        </a>
                        <a
                          className="agr-scan__iconbtn"
                          href={scan.downloadUrl}
                          download={scan.originalName}
                          title="Скачать"
                        >
                          <Icon name="download" size={14} />
                        </a>
                        <button
                          type="button"
                          className="agr-scan__textbtn"
                          onClick={onPickFile}
                          disabled={uploading}
                          title="Заменить"
                        >
                          Заменить
                        </button>
                        <button
                          type="button"
                          className="agr-scan__iconbtn agr-scan__iconbtn--danger"
                          onClick={onRemoveScan}
                          disabled={uploading}
                          title="Удалить скан"
                        >
                          <Icon name="x" size={14} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className={'agr-scan__empty' + (dragOver ? ' is-dragover' : '')}
                      onClick={onPickFile}
                      onDragOver={onDragOver}
                      onDragLeave={onDragLeave}
                      onDrop={onDrop}
                    >
                      <Icon name="download" size={14} />
                      <span>
                        {uploading
                          ? 'Загружаем…'
                          : (dragOver
                              ? 'Отпустите, чтобы загрузить'
                              : 'Перетащите файл сюда или нажмите, чтобы выбрать (PDF / JPG / PNG, до 50 МБ)')}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* === Документы (Word-шаблоны) — только в edit-режиме === */}
              {isEdit && (
                <DocumentTemplatesBlock agreement={agreement} />
              )}

              {/* === Привязка к 1С (только в edit-режиме) === */}
              {isEdit && (
                <div className="agr-onec">
                  <div className="agr-onec__title">Привязка к 1С</div>
                  {agreement?.onec?.code ? (
                    <div className="agr-onec__card">
                      <div className="agr-onec__icon" aria-hidden>
                        <Icon name="link" size={18} />
                      </div>
                      <div className="agr-onec__main">
                        <div className="agr-onec__head">
                          Договор 1С: <span className="mono agr-onec__code">{agreement.onec.code}</span>
                        </div>
                        {student?.onecContractor?.code && (
                          <div className="agr-onec__sub">
                            Контрагент: {student.onecContractor.name}
                            {' · '}
                            <span className="mono">{student.onecContractor.code}</span>
                          </div>
                        )}
                      </div>
                      <div className="agr-onec__actions">
                        <button
                          type="button"
                          className="agr-onec__textbtn"
                          onClick={() => { onLink?.(agreement); onClose?.(); }}
                          title="Изменить привязку"
                        >
                          <Icon name="edit" size={12} /> Изменить
                        </button>
                        <button
                          type="button"
                          className="agr-onec__textbtn"
                          onClick={async () => {
                            if (!confirm('Отвязать договор от 1С? Запись в кэше 1С останется.')) return;
                            try {
                              const res = await api.delete(`/api/agreements/${agreement.id}/onec-link`);
                              onSaved?.(res.agreement);
                            } catch (err) {
                              setError(err instanceof ApiError ? err.message : 'Не удалось отвязать');
                            }
                          }}
                          title="Отвязать"
                        >
                          <Icon name="link-off" size={12} /> Отвязать
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="agr-onec__card agr-onec__card--empty">
                      <div className="agr-onec__icon agr-onec__icon--muted" aria-hidden>
                        <Icon name="link-off" size={18} />
                      </div>
                      <div className="agr-onec__main">
                        <div className="agr-onec__head">Не привязан к 1С</div>
                        {agreement?.onec?.syncState === 'pending_create' && (
                          <div className="agr-onec__sub">Ожидает синхронизации…</div>
                        )}
                        {agreement?.onec?.syncState === 'error' && agreement?.onec?.syncError && (
                          <div className="agr-onec__sub agr-onec__sub--error">
                            {agreement.onec.syncError}
                          </div>
                        )}
                      </div>
                      <div className="agr-onec__actions">
                        <button
                          type="button"
                          className="agr-onec__textbtn"
                          onClick={() => { onLink?.(agreement); onClose?.(); }}
                        >
                          <Icon name="link" size={12} /> Привязать
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* === Сводка справа === */}
            <aside className="agr-modal__summary">
              <div className="agr-summary__label">Сводка</div>
              <div className="agr-summary__head">
                <div className={'agr-summary__icon agr-summary__icon--' + type}>
                  <Icon name={typeMeta.icon} size={28} />
                </div>
                <div className="agr-summary__title">{typeMeta.label}</div>
                {(number || (isEdit && agreement?.number)) && (
                  <div className="agr-summary__num">
                    {Agreement_displayNumber(type, number || (agreement?.number ?? ''))}
                  </div>
                )}
                {(dateStart || dateEnd) && (
                  <div className="agr-summary__period">
                    {fmtDateRu(dateStart)} — {fmtDateRu(dateEnd)}
                    {showClass && klass !== '' && <> · {klass} «А»</>}
                  </div>
                )}
                {terminated && (
                  <div className="agr-summary__pill">
                    <Icon name="x" size={10} /> РАСТОРГНУТ {fmtDateRu(terminationDate)}
                  </div>
                )}
              </div>

              {showTariff && tariff && (
                <>
                  <div className="agr-summary__row">Плата/мес <b>{fmtMoney(tariff.price)}</b></div>
                  {admissionFee !== '' && Number(admissionFee) > 0 && (
                    <div className="agr-summary__row">Вст. взнос <b>{fmtMoney(admissionFee)}</b></div>
                  )}
                  {costBreakdown.monthsCount > 0 && (
                    <div className="agr-summary__total">
                      <span>
                        За учебный год<br/>
                        <span style={{ fontSize: 11, color: 'var(--fg-faint)' }}>
                          {costBreakdown.monthsCount} мес.
                          {costBreakdown.months.some((x) => !x.isFullMonth) && (
                            <span title="Есть неполные месяцы — оплата пропорционально рабочим дням">
                              {' · с пропорц.'}
                            </span>
                          )}
                        </span>
                      </span>
                      <b>{fmtMoney(costBreakdown.total)}</b>
                    </div>
                  )}
                </>
              )}

              {isEdit && agreement?.onec?.code && (
                <>
                  <div className="agr-summary__label" style={{ marginTop: 6 }}>1С</div>
                  <div className="agr-summary__row" style={{ alignItems: 'center' }}>
                    <span className="mono" style={{ color: 'var(--fg)' }}>1С: {agreement.onec.code}</span>
                  </div>
                </>
              )}

              {signatoryMode === 'existing' && pickedContact && (
                <>
                  <div className="agr-summary__label" style={{ marginTop: 6 }}>Заказчик</div>
                  <div style={{ fontSize: 12.5, color: 'var(--fg)' }}>{pickedContact.fullName}</div>
                  {pickedContact.phone && (
                    <div style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>{pickedContact.phone}</div>
                  )}
                </>
              )}
            </aside>
            </div>

            <div className="sd-edit__foot">
              {error && <div className="sd-edit__error">{error}</div>}
              {isEdit && (
                <button
                  type="button"
                  className="btn btn--sm btn--danger"
                  onClick={onDeleteAgreement}
                  disabled={saving}
                  title="Удалить договор"
                >
                  Удалить
                </button>
              )}
              <div className="sd-edit__foot-spacer" />
              <button type="button" className="btn btn--sm" onClick={onClose}>Отмена</button>
              <button type="submit" className="btn btn--primary btn--sm" disabled={saving}>
                {saving ? 'Сохраняем…' : (isEdit ? 'Сохранить' : 'Создать')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// Список Word-шаблонов, доступных для договора. Зависит от type.
const TEMPLATES_BY_TYPE = {
  school: [
    { code: 'agreement_school',                         label: 'Договор обучения' },
    { code: 'application_for_admission__school',        label: 'Заявление на приём' },
    { code: 'agreement_termination',                    label: 'Заявление на расторжение' },
  ],
  kindergarten: [
    { code: 'agreement_kindergarten',                   label: 'Договор обучения' },
    { code: 'application_for_admission__kindergarten',  label: 'Заявление на приём' },
    { code: 'agreement_termination',                    label: 'Заявление на расторжение' },
  ],
  paid_services: [
    { code: 'agreement_paid_services',                  label: 'Договор на доп. услуги' },
    { code: 'agreement_termination',                    label: 'Заявление на расторжение' },
  ],
};

function DocumentTemplatesBlock({ agreement }) {
  const list = TEMPLATES_BY_TYPE[agreement?.type] || [];
  if (!list.length) return null;
  return (
    <div className="agr-templates">
      <div className="agr-templates__title">Документы</div>
      <div className="agr-templates__list">
        {list.map((t) => (
          <a
            key={t.code}
            href={`/api/agreements/${agreement.id}/template/${t.code}`}
            className="agr-templates__btn"
            title="Скачать .docx с подставленными данными"
          >
            <Icon name="download" size={13} />
            <span>{t.label}</span>
          </a>
        ))}
      </div>
    </div>
  );
}

// Иконка файла «лист с загнутым уголком» + цветной бейдж типа в углу.
function ScanFileIcon({ mime }) {
  const isPdf = (mime || '').startsWith('application/pdf');
  const isImg = (mime || '').startsWith('image/');
  const tag = isPdf ? 'PDF' : (isImg ? 'IMG' : 'FILE');
  const tagClass = isPdf ? 'agr-scan__tag--pdf' : (isImg ? 'agr-scan__tag--img' : 'agr-scan__tag--file');
  return (
    <div className="agr-scan__filemark" aria-hidden>
      <svg viewBox="0 0 40 48" width="40" height="48" fill="none">
        <path
          d="M4 2h22l10 10v32a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"
          fill="white"
          stroke="currentColor"
          strokeWidth="1.4"
        />
        <path d="M26 2v8a2 2 0 0 0 2 2h8" stroke="currentColor" strokeWidth="1.4" fill="none" />
        <path d="M9 22h22M9 27h22M9 32h16" stroke="currentColor" strokeOpacity="0.35" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
      <span className={'agr-scan__tag ' + tagClass}>{tag}</span>
    </div>
  );
}
